import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { axiosServiceInstance } from '@onesource/services';
import { Link, useLocation } from '@tanstack/react-router';
import { useState } from 'react';
import { RiExternalLinkLine, RiLockLine, RiLockUnlockLine } from 'react-icons/ri';
import type { SidebarItem, SidebarSubItem } from './types';

interface OttGenerationResponse {
  ott: string;
  redirectUrl: string;
  expiresIn: number;
}

interface SubmenuPanelProps {
  item: SidebarItem;
}

interface SubItemProps {
  subItem: SidebarSubItem;
}

const SubItem = ({ subItem }: SubItemProps) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);

  const isActive = subItem.path ? location.pathname.startsWith(subItem.path) : false;

  const theme = useTheme();

  // Handle external app click with OTT generation
  const handleExternalAppClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (!subItem.externalApp || loading) {
      return;
    }

    setLoading(true);
    try {
      const response = await axiosServiceInstance.post<OttGenerationResponse>('/auth/generate-ott/user', {
        targetApp: subItem.externalApp.appId,
        redirectUrl: subItem.externalApp.url,
      });
      const anchor = document.createElement('a');
      anchor.href = response.data.redirectUrl;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      document.body.append(anchor);
      anchor.click();
      anchor.remove();
    } catch (error) {
      console.error('Failed to generate OTT:', error);
    } finally {
      setLoading(false);
    }
  };

  const buttonContent = (
    <ListItemButton
      onClick={subItem.externalApp ? handleExternalAppClick : undefined}
      disabled={loading}
      sx={{
        minHeight: 20,
        py: 0.5,
        px: 0.5,
        borderRadius: '5px',
        justifyContent: 'space-between',
        '&:hover': {
          bgcolor: 'action.hover',
        },
        ...(isActive && {
          bgcolor: '#FFE9DB66',
        }),
      }}
    >
      <ListItemText
        primary={<span dangerouslySetInnerHTML={{ __html: subItem.label }} />}
        primaryTypographyProps={{
          sx: {
            px: 0.5,
            fontSize: 12,
            fontWeight: isActive ? 700 : 400,
            color: subItem.isLocked ? '#B3B3B3' : '#0F0F0F',
          },
        }}
      />
      {loading ? (
        <CircularProgress size={12} sx={{ ml: 1 }} />
      ) : subItem.externalApp ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'grey.500',
            ml: 1,
          }}
        >
          <RiExternalLinkLine size={12} color={theme.palette.grey[900]} />
        </Box>
      ) : (
        subItem.isLocked !== undefined && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: subItem.isLocked ? '#B3B3B3' : 'grey.500',
              ml: 1,
            }}
          >
            {subItem.isLocked ? (
              <RiLockLine size={12} color={theme.palette.grey[900]} />
            ) : (
              <RiLockUnlockLine size={12} color={theme.palette.grey[900]} />
            )}
          </Box>
        )
      )}
    </ListItemButton>
  );

  // External apps don't use Link - just the button with click handler
  if (subItem.externalApp) {
    return buttonContent;
  }

  // Internal paths use Link for navigation
  if (subItem.path) {
    return (
      <Link to={subItem.path} style={{ textDecoration: 'none', color: 'inherit' }}>
        {buttonContent}
      </Link>
    );
  }

  // Fallback for items without path or externalApp
  return buttonContent;
};

export const SubmenuPanel = ({ item }: SubmenuPanelProps) => {
  if (!item.children || item.children.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        ml: 0.3,
        position: 'absolute',
        left: 70,
        top: 0,
        width: 245,
        minHeight: '100%',
        bgcolor: '#fafafa',
        borderRadius: '0 20px 0 0',
        boxShadow: '14px 17px 40px 0px rgba(112,144,176,0.08)',
        py: 4.75,
        px: 1.5,
        zIndex: 10,
      }}
    >
      <Box sx={{ mb: 1 }}>
        <Typography
          component="span"
          sx={{
            fontSize: 12,
            fontWeight: 700,
            color: '#0F0F0F',
            bgcolor: '#FFEEE8',
            px: 0.8,
            py: 0.25,
            borderRadius: '5px',
          }}
        >
          {item.label}
        </Typography>
      </Box>
      <List disablePadding sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {item.children.map(subItem => (
          <SubItem key={subItem.id} subItem={subItem} />
        ))}
      </List>
    </Box>
  );
};
